import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Row, Col } from "react-bootstrap";
import { Formik, Field } from "formik";
import * as yup from "yup";
import { Popover as TinyPopover } from "react-tiny-popover";
import VehicleSelectorTable from "./VehicleSelectorTable";
import DriverSelectorTable from "./DriverSelectorTable";
import RouteSelectorTable from "./RouteSelectorTable";
import SearchIcon from "mdi-react/SearchIcon";
import { CustomInput } from "../utils/CustomInput";

const TripModal = ({ show, isEdit, onHide, editTrip, onSubmit, isLoading }) => {
  const [showVehiclePopover, setShowVehiclePopover] = useState(false);
  const [showDriverPopover, setShowDriverPopover] = useState(false);
  const [showRoutePopover, setShowRoutePopover] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);

  const TRIPSTAGES = {
    create: ["planned"],
    start: ["in_progress", "planned"],
    inProgress: ["in_progress", "completed"],
    completed: ["cancelled", "completed"],
    all: ["planned", "in_progress", "completed", "cancelled"],
  };

  if (isEdit && !editTrip) return null;

  // Dynamic validation schema based on status
  const tripSchema = yup.object().shape({
    vehicle_id: yup
      .string()
      .required("Vehicle is required")
      .min(3, "Vehicle ID must be at least 3 characters"),
    driver_id: yup
      .string()
      .required("Driver is required")
      .min(3, "Driver ID must be at least 3 characters"),
    route_id: yup.string().nullable(),
    tripNumber: yup
      .string()
      .max(50, "Trip number must not exceed 50 characters"),
    plannedStartTime: yup
      .string()
      .required("Planned start time is required")
      .test("is-valid-date", "Invalid date format", (value) => {
        return value ? !isNaN(new Date(value).getTime()) : true;
      }),
    plannedEndTime: yup
      .string()
      .required("Planned end time is required")
      .test("is-valid-date", "Invalid date format", (value) => {
        return value ? !isNaN(new Date(value).getTime()) : true;
      })
      .test("is-after-start", "End time must be after start time", function(
        value
      ) {
        const { plannedStartTime } = this.parent;
        if (!plannedStartTime || !value) return true;
        return new Date(value) > new Date(plannedStartTime);
      }),
    actualStartTime: yup.string().when("status", {
      is: (status) => TRIPSTAGES.inProgress.includes(status),
      then: (schema) =>
        schema
          .required("Actual start time is required for this status")
          .test("is-valid-date", "Invalid date format", (value) => {
            return value ? !isNaN(new Date(value).getTime()) : true;
          }),
      otherwise: (schema) => schema.nullable(),
    }),
    actualEndTime: yup.string().when("status", {
      is: "completed",
      then: (schema) =>
        schema
          .required("Actual end time is required for completed trips")
          .test("is-valid-date", "Invalid date format", (value) => {
            return value ? !isNaN(new Date(value).getTime()) : true;
          })
          .test(
            "is-after-actual-start",
            "End time must be after start time",
            function(value) {
              const { actualStartTime } = this.parent;
              if (!actualStartTime || !value) return true;
              return new Date(value) > new Date(actualStartTime);
            }
          ),
      otherwise: (schema) => schema.nullable(),
    }),
    plannedDistance: yup
      .number()
      .nullable()
      .positive("Distance must be a positive number")
      .max(99999, "Distance seems unrealistic")
      .typeError("Distance must be a number"),
    actualDistance: yup
      .number()
      .nullable()
      .positive("Distance must be a positive number")
      .max(99999, "Distance seems unrealistic")
      .typeError("Distance must be a number"),
    startOdometer: yup
      .number()
      .nullable()
      .min(0, "Odometer must be 0 or greater")
      .typeError("Odometer must be a number"),
    endOdometer: yup.number().nullable().when("status", {
      is: "completed",
      then: (schema) =>
        schema
          .required("End odometer is required for completed trips")
          .positive("Odometer number must be a positive number")
          .typeError("Odometer must be a number")
          .test(
            "is-after-start",
            "End Odometer must be greater than start Odometer",
            function(value) {
              const { startOdometer } = this.parent;
              if (startOdometer == null || value == null) return true;
              return Number(value) > Number(startOdometer);
            }
          ),
      otherwise: (schema) => schema.nullable(),
    }),
    status: yup
      .string()
      .required("Status is required")
      .oneOf(TRIPSTAGES.all, "Invalid status"),
    purpose: yup
      .string()
      .max(500, "Purpose must not exceed 500 characters")
      .nullable(),
    notes: yup
      .string()
      .max(1000, "Notes must not exceed 1000 characters")
      .nullable(),
  });

  const initialValues = editTrip
    ? {
        vehicle_id: editTrip.vehicle_id || "",
        driver_id: editTrip.driver_id || "",
        route_id: editTrip.route_id || "",
        tripNumber: editTrip.tripNumber || "",
        plannedStartTime: editTrip.plannedStartTime
          ? editTrip.plannedStartTime.slice(0, 16)
          : "",
        plannedEndTime: editTrip.plannedEndTime
          ? editTrip.plannedEndTime.slice(0, 16)
          : "",
        actualStartTime: editTrip.actualStartTime
          ? editTrip.actualStartTime.slice(0, 16)
          : "",
        actualEndTime: editTrip.actualEndTime
          ? editTrip.actualEndTime.slice(0, 16)
          : "",
        startOdometer: editTrip.startOdometer || 0,
        endOdometer: editTrip.endOdometer || 0,
        plannedDistance: editTrip.plannedDistance || "",
        actualDistance:
          editTrip.actualDistance || editTrip.plannedDistance || "",
        status: editTrip.status || "planned",
        purpose: editTrip.purpose || "",
        notes: editTrip.notes || "",
      }
    : {
        vehicle_id: "",
        driver_id: "",
        route_id: "",
        tripNumber: "",
        plannedStartTime: "",
        plannedEndTime: "",
        actualStartTime: "",
        actualEndTime: "",
        plannedDistance: "",
        actualDistance: "",
        startOdometer: 0,
        endOdometer: 0,
        status: "planned",
        purpose: "",
        notes: "",
      };


  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{editTrip ? "Edit Trip" : "Add New Trip"}</Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={initialValues}
        validationSchema={tripSchema}
        onSubmit={(values) => {
          console.log("submitting values", values)
          onSubmit({
            ...values,
            vehicle_id: selectedVehicle.vehicle_id,
            driver_id: selectedDriver.driver_id,
            route_id: selectedRoute.route_id,
          });
        }}
        enableReinitialize
      >
        {({
          values,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          touched,
          errors,
        }) => {
          console.log(errors)
          return <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                {/* Vehicle Selection */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Vehicle <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="input-group">
                      <TinyPopover
                        isOpen={showVehiclePopover}
                        position="bottom"
                        content={() => (
                          <VehicleSelectorTable
                            handleSelectedItem={(vehicle) => {
                              setFieldValue(
                                "vehicle_id",
                                `${vehicle.vin} - ${vehicle.model}`
                              );
                              setSelectedVehicle(vehicle);
                              setFieldValue("startOdometer", vehicle.odometer);
                              setShowVehiclePopover(false);
                            }}
                          />
                        )}
                        onClickOutside={() => setShowVehiclePopover(false)}
                      >
                        <>
                          <CustomInput
                            className="p-cursor"
                            name="vehicle_id"
                            placeholder="Select vehicle"
                            typeOfInput="text"
                            value={values.vehicle_id}
                            disabled
                            onChange={handleChange}
                            onClick={() =>
                              setShowVehiclePopover(!showVehiclePopover)
                            }
                          />
                          <span
                            className="input-group-text border p-cursor"
                            onClick={() =>
                              setShowVehiclePopover(!showVehiclePopover)
                            }
                            title="Search vehicle to set on trip"
                          >
                            <SearchIcon />
                          </span>
                        </>
                      </TinyPopover>
                    </div>
                    {touched.vehicle_id && errors.vehicle_id ? (
                      <div className="text-danger small mt-1">
                        {errors.vehicle_id}
                      </div>
                    ) : null}
                  </Form.Group>
                </Col>

                {/* Driver Selection */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Driver <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="input-group">
                      <TinyPopover
                        isOpen={showDriverPopover}
                        position="bottom"
                        content={
                          <DriverSelectorTable
                            handleSelectedItem={(driver) => {
                              console.log(driver);
                              setSelectedDriver(driver);
                              setFieldValue(
                                "driver_id",
                                `${driver.Title} ${driver.Name}`
                              );
                              setShowDriverPopover(false);
                            }}
                          />
                        }
                        onClickOutside={() => setShowDriverPopover(false)}
                      >
                        <>
                          <CustomInput
                            className="p-cursor"
                            name="driver_id"
                            placeholder="Select driver"
                            typeOfInput="text"
                            value={values.driver_id}
                            disabled
                            onChange={handleChange}
                            onClick={() =>
                              setShowDriverPopover(!showDriverPopover)
                            }
                          />
                          <span
                            className="input-group-text border p-cursor"
                            onClick={() =>
                              setShowDriverPopover(!showDriverPopover)
                            }
                            title="Search driver to set on trip"
                          >
                            <SearchIcon />
                          </span>
                        </>
                      </TinyPopover>
                    </div>
                    {touched.driver_id && errors.driver_id ? (
                      <div className="text-danger small mt-1">
                        {errors.driver_id}
                      </div>
                    ) : null}
                  </Form.Group>
                </Col>

                {/* Route Selection */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Route (Optional)</Form.Label>
                    <div className="input-group">
                      <TinyPopover
                        isOpen={showRoutePopover}
                        position="bottom"
                        content={
                          <RouteSelectorTable
                            handleSelectedItem={(route) => {
                              setFieldValue("route_id", route.name);
                              setFieldValue("plannedDistance", route.distance);
                              setSelectedRoute(route)
                              setShowRoutePopover(false);
                            }}
                          />
                        }
                        onClickOutside={() => setShowRoutePopover(false)}
                      >
                        <>
                          <CustomInput
                            className="p-cursor"
                            name="route_id"
                            placeholder="Select route"
                            typeOfInput="text"
                            value={values.route_id}
                            disabled
                            onChange={handleChange}
                            onClick={() =>
                              setShowRoutePopover(!showRoutePopover)
                            }
                          />
                          <span
                            className="input-group-text border p-cursor"
                            onClick={() =>
                              setShowRoutePopover(!showRoutePopover)
                            }
                            title="Search route to set on trip"
                          >
                            <SearchIcon />
                          </span>
                        </>
                      </TinyPopover>
                    </div>
                    {touched.route_id && errors.route_id ? (
                      <div className="text-danger small mt-1">
                        {errors.route_id}
                      </div>
                    ) : null}
                  </Form.Group>
                </Col>

                {/* Trip Number */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Trip Number</Form.Label>
                    <Field
                      as={Form.Control}
                      type="text"
                      name="tripNumber"
                      placeholder="Auto-generated if empty"
                    />
                    {touched.tripNumber && errors.tripNumber ? (
                      <div className="text-danger small mt-1">
                        {errors.tripNumber}
                      </div>
                    ) : null}
                  </Form.Group>
                </Col>

                {/* Status */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Status <span className="text-danger">*</span>
                    </Form.Label>
                    <Field as={Form.Select} name="status">
                      <option value="planned">Planned</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </Field>
                    {touched.status && errors.status ? (
                      <div className="text-danger small mt-1">
                        {errors.status}
                      </div>
                    ) : null}
                  </Form.Group>
                </Col>

                {/* Planned Start Time */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Planned Start Time <span className="text-danger">*</span>
                    </Form.Label>
                    <Field
                      as={Form.Control}
                      type="datetime-local"
                      name="plannedStartTime"
                    />
                    {touched.plannedStartTime && errors.plannedStartTime ? (
                      <div className="text-danger small mt-1">
                        {errors.plannedStartTime}
                      </div>
                    ) : null}
                  </Form.Group>
                </Col>

                {/* Planned End Time */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Planned End Time <span className="text-danger">*</span>
                    </Form.Label>
                    <Field
                      as={Form.Control}
                      type="datetime-local"
                      name="plannedEndTime"
                    />
                    {touched.plannedEndTime && errors.plannedEndTime ? (
                      <div className="text-danger small mt-1">
                        {errors.plannedEndTime}
                      </div>
                    ) : null}
                  </Form.Group>
                </Col>

                {/* Actual Start Time - Show when in_progress or completed */}
                {TRIPSTAGES.inProgress.includes(values.status) && (
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Actual Start Time <span className="text-danger">*</span>
                      </Form.Label>
                      <Field
                        as={Form.Control}
                        type="datetime-local"
                        name="actualStartTime"
                      />
                      {touched.actualStartTime && errors.actualStartTime ? (
                        <div className="text-danger small mt-1">
                          {errors.actualStartTime}
                        </div>
                      ) : null}
                    </Form.Group>
                  </Col>
                )}

                {/* Actual End Time - Show only when completed */}
                {TRIPSTAGES.completed.includes(values.status) && (
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Actual End Time <span className="text-danger">*</span>
                      </Form.Label>
                      <Field
                        as={Form.Control}
                        type="datetime-local"
                        name="actualEndTime"
                      />
                      {touched.actualEndTime && errors.actualEndTime ? (
                        <div className="text-danger small mt-1">
                          {errors.actualEndTime}
                        </div>
                      ) : null}
                    </Form.Group>
                  </Col>
                )}

                {/* Planned Distance */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Planned Distance (km)</Form.Label>
                    <Field
                      as={Form.Control}
                      type="number"
                      step="0.01"
                      name="plannedDistance"
                      placeholder="e.g., 150.5"
                    />
                    {touched.plannedDistance && errors.plannedDistance ? (
                      <div className="text-danger small mt-1">
                        {errors.plannedDistance}
                      </div>
                    ) : null}
                  </Form.Group>
                </Col>

                {/* start Odometer */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Odometer</Form.Label>
                    <Field
                      as={Form.Control}
                      type="number"
                      step="0.01"
                      disabled={TRIPSTAGES.start.includes(editTrip?.status)}
                      name="startOdometer"
                      placeholder="e.g., 120.5"
                    />
                    {touched.startOdometer && errors.startOdometer ? (
                      <div className="text-danger small mt-1">
                        {errors.startOdometer}
                      </div>
                    ) : null}
                  </Form.Group>
                </Col>

                {/* End Odometer */}
                {values.status === "completed" && (
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>End Odometer</Form.Label>
                      <Field
                        as={Form.Control}
                        type="number"
                        step="0.01"
                        name="endOdometer"
                        placeholder="e.g., 180.5"
                      />
                      {touched.endOdometer && errors.endOdometer ? (
                        <div className="text-danger small mt-1">
                          {errors.endOdometer}
                        </div>
                      ) : null}
                    </Form.Group>
                  </Col>
                )}

                {/* Actual Distance - Show when completed */}
                {values.status === "completed" && (
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Actual Distance (km)</Form.Label>
                      <Field
                        as={Form.Control}
                        type="number"
                        step="0.01"
                        name="actualDistance"
                        placeholder="e.g., 155.2"
                      />
                      {touched.actualDistance && errors.actualDistance ? (
                        <div className="text-danger small mt-1">
                          {errors.actualDistance}
                        </div>
                      ) : null}
                    </Form.Group>
                  </Col>
                )}

                {/* Purpose */}
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Purpose</Form.Label>
                    <Field
                      as={Form.Control}
                      type="text"
                      name="purpose"
                      placeholder="e.g., Intercity cargo delivery"
                    />
                    {touched.purpose && errors.purpose ? (
                      <div className="text-danger small mt-1">
                        {errors.purpose}
                      </div>
                    ) : null}
                  </Form.Group>
                </Col>

                {/* Notes */}
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Notes</Form.Label>
                    <Field
                      as={Form.Control}
                      // as="textarea"
                      rows={3}
                      name="notes"
                      placeholder="Additional notes or comments about this trip"
                    />
                    {touched.notes && errors.notes ? (
                      <div className="text-danger small mt-1">
                        {errors.notes}
                      </div>
                    ) : null}
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={onHide}
                disabled={isSubmitting || isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting || isLoading}
              >
                {isSubmitting || isLoading
                  ? editTrip
                    ? "Updating..."
                    : "Creating..."
                  : editTrip
                  ? "Update Trip"
                  : "Create Trip"}
              </Button>
            </Modal.Footer>
          </Form>
        }}
      </Formik>
    </Modal>
  );
};

export default TripModal;
